import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { tableColumnPropType } from '../utils/propTypes';
import { ArrowDownIcon } from '@pattern-library/icon';
import CheckboxCell from './CheckboxCell';
const propTypes = {
  /**
   * Column metadata
   */
  columns: PropTypes.arrayOf(tableColumnPropType).isRequired,
  /**
   * An array of information that defines column groupings
   *
   * ```
   * {
   *     label: string,
   *     groupId: string
   * }
   * ```
   */
  columnGroupings: PropTypes.array,
  /**
   * The number of columns docked to the left of the table
   */
  fixedColumnCount: PropTypes.number.isRequired,
  /**
   * Function to call when the select all checkbox is toggled
   */
  onSelectAll: PropTypes.func,
  /**
   * Function to call when the sort state changes
   */
  onSortChange: PropTypes.func.isRequired,
  /**
   * Flag to determine if all rows are selected
   */
  selectAll: PropTypes.bool,
  /**
   * Object that defines the current sort state
   */
  sortState: PropTypes.object,
  /**
   * Object of theme-like props that will not change after the component mounts
   */
  theme: PropTypes.shape({
    /**
     * Table background color
     */
    backgroundColor: PropTypes.oneOf(['white', 'gray-05', 'gray-10']),
    /**
     * Flag to determine if the column headers are bold or regular weight
     */
    boldColumnHeaders: PropTypes.bool,
    /**
     * Dock the header to the top or the footer to the bottom when scrolling
     */
    fixedRows: PropTypes.oneOf(['header', 'footer', 'both', ''])
  })
};
function getNextSortOrder(currentSort, dataKey) {
  // If nothing has been sorted, return desc
  if (!currentSort) return 'asc';
  if (currentSort.key === dataKey) {
    return currentSort.order === 'desc' ? 'asc' : 'desc';
  }

  // default to descending
  return 'asc';
}
function TableHeader(_ref) {
  let {
    columnGroupings,
    columns,
    fixedColumnCount,
    onSelectAll,
    onSortChange,
    selectAll,
    sortState,
    theme = {
      backgroundColor: 'white',
      boldColumnHeaders: true,
      fixedRows: ''
    }
  } = _ref;
  const {
    backgroundColor,
    boldColumnHeaders,
    fixedRows
  } = theme;
  const isFixed = fixedRows === 'header' || fixedRows === 'both';
  const headerRowClassNames = cx('asc-table-component-header', {
    'asc-table-component-header--fixed': isFixed
  });
  const headerGroupRowClassNames = cx('asc-table-component-header-group', {
    'asc-table-component-header-group--fixed': isFixed
  });
  const getCellClassNames = (column, isLast) => {
    return cx(`asc-table--bg-${backgroundColor}`, {
      'asc-table-component-cell--fixed': column.fixed,
      'asc-table-component-cell--fixed-last': isLast,
      'asc-table-component-cell--sortable': column.sortable,
      'asc-table-hidden': column.hidden || column.rowHeader,
      'asc-font-regular': !boldColumnHeaders
    });
  };
  const getColumnGroupMarkup = (groupOutput, defaultOutput) => {
    return columns.map((column, index) => {
      if (column.groupId) {
        if (index === 0 || column.groupId !== columns[index - 1].groupId) {
          let colspan = columns.filter(col => col.groupId === column.groupId).length;
          return groupOutput(colspan, index, column);
        }
        return undefined;
      } else {
        return defaultOutput(index, column);
      }
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, columnGroupings && /*#__PURE__*/React.createElement("colgroup", null, getColumnGroupMarkup((colspan, index) => /*#__PURE__*/React.createElement("col", {
    key: 'colgroup-' + index,
    span: colspan
  }), index => /*#__PURE__*/React.createElement("col", {
    key: 'colgroup-' + index
  }))), /*#__PURE__*/React.createElement("thead", null, columnGroupings && /*#__PURE__*/React.createElement("tr", {
    className: headerGroupRowClassNames
  }, getColumnGroupMarkup((colspan, index, column) => {
    return /*#__PURE__*/React.createElement("th", {
      key: 'col-grouping-' + index,
      colSpan: colspan,
      className: getCellClassNames(column, index + colspan === fixedColumnCount),
      scope: "colgroup",
      style: {
        left: column.left,
        width: column.width
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "asc-table-component-group"
    }, /*#__PURE__*/React.createElement("hr", {
      "aria-hidden": true
    }), columnGroupings.find(group => group.groupId === column.groupId).label, /*#__PURE__*/React.createElement("hr", {
      "aria-hidden": true
    })));
  }, (index, column) => {
    return /*#__PURE__*/React.createElement("th", {
      key: 'col-grouping-' + index,
      className: getCellClassNames(column, index === fixedColumnCount - 1),
      scope: "col"
    });
  })), /*#__PURE__*/React.createElement("tr", {
    className: headerRowClassNames
  }, columns.map((column, index) => {
    const align = column.align ? column.align : 'left';
    let cellClassNames = getCellClassNames(column, index === fixedColumnCount - 1);
    let cellStyles = {
      textAlign: align,
      minWidth: column.width ? column.width : 'auto'
    };
    if (column.fixed && column.left) {
      cellStyles.left = column.left;
    }
    const nextSortOrder = getNextSortOrder(sortState, column.dataKey);
    const isActiveSort = sortState && sortState.key === column.dataKey;
    let ariaSort;
    let headerCellContent;
    if (column.sortable && column.header) {
      ariaSort = isActiveSort ? nextSortOrder === 'asc' ? 'descending' : 'ascending' : undefined;
      headerCellContent = /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: cx('asc-table-component-header-sorter', `asc-table-component-header-sorter--${align}`, isActiveSort && (nextSortOrder === 'asc' ? 'asc-table-component-header-sorter--desc' : 'asc-table-component-header-sorter--asc')),
        title: column.header,
        onClick: () => onSortChange(column.dataKey, nextSortOrder)
      }, /*#__PURE__*/React.createElement("span", {
        className: "asc-table-component-header-sorter-contents",
        style: {
          justifyContent: align === 'left' ? 'flex-start' : align === 'center' ? align : 'flex-end'
        }
      }, column.header.toUpperCase(), isActiveSort && /*#__PURE__*/React.createElement(ArrowDownIcon, {
        color: "current",
        size: "xxs",
        label: nextSortOrder === 'desc' ? 'up arrow' : 'down arrow'
      })));
    } else if (column.dataKey === 'select') {
      headerCellContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: "asc-acc-hidden"
      }, column.header?.toUpperCase()), onSelectAll && /*#__PURE__*/React.createElement(CheckboxCell, {
        id: "select-all",
        label: "Select All Rows",
        onChange: (label, checked) => onSelectAll(!checked),
        value: selectAll,
        qaId: "select-all"
      }));
    } else if (column.hidden || column.rowHeader || column.hiddenHeader) {
      headerCellContent = /*#__PURE__*/React.createElement("span", {
        className: "asc-acc-hidden"
      }, column.header?.toUpperCase());
    } else {
      headerCellContent = !column.sortable && column.dataKey !== 'select' && column.header?.toUpperCase();
    }
    return /*#__PURE__*/React.createElement("th", {
      id: 'col-header-' + column.dataKey,
      key: 'col-header-' + column.dataKey,
      className: cellClassNames,
      style: cellStyles,
      scope: "col",
      "aria-sort": ariaSort
    }, headerCellContent);
  }))));
}
TableHeader.propTypes = propTypes;
export default TableHeader;